import React, { useEffect, useState } from 'react';
import { logger } from '@/lib/logger';
import { EnhancedFloorPlanViewer } from './EnhancedFloorPlanViewer';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { FloorPlan } from '@/types/floorPlanViewer';

interface FloorPlanViewerWrapperProps {
  floorPlanId: string;
  onBack?: () => void;
  viewMode?: 'design' | 'presentation';
  onViewModeChange?: (mode: 'design' | 'presentation') => void;
}

export function FloorPlanViewerWrapper({
  floorPlanId,
  onBack,
  viewMode = 'design',
  onViewModeChange,
}: FloorPlanViewerWrapperProps) {
  const [floorPlan, setFloorPlan] = useState<FloorPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFloorPlanData();
  }, [floorPlanId]);

  const loadFloorPlanData = async () => {
    setIsLoading(true);
    try {
      // Load floor plan
      const supabase = await getAuthenticatedSupabaseClient();
      const { data: floorPlanData, error: floorPlanError } = await supabase
        .from('floor_plans')
        .select('*')
        .eq('id', floorPlanId)
        .single();

      if (floorPlanError) throw floorPlanError;
      setFloorPlan(floorPlanData);
    } catch (error) {
      logger.error('Error loading floor plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!floorPlan) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Floor plan not found</p>
      </div>
    );
  }

  // Use the enhanced viewer for all floor plans
  return (
    <EnhancedFloorPlanViewer
      floorPlan={floorPlan}
      onBack={onBack}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
    />
  );
}
